import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/api";
import { PageHeader } from "@/pages/admin/AdminDashboard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Plus, Trash2, Users, Calendar } from "lucide-react";
import { JOURS_LONGS, MOIS } from "@/lib/dates";

export default function AdminFormations() {
  const [sessions, setSessions] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ date: "", time: "10:00", title: "", duree: "2h", mode: "Visioconférence", description: "", places_total: 8 });

  const load = async () => {
    try {
      const [s, b] = await Promise.all([
        api.get("/admin/formations/sessions"),
        api.get("/admin/formations/bookings"),
      ]);
      setSessions(s.data);
      setBookings(b.data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    try {
      const iso = new Date(`${form.date}T${form.time}:00`).toISOString();
      await api.post("/admin/formations/sessions", {
        date: iso, title: form.title, duree: form.duree, mode: form.mode,
        description: form.description, places_total: parseInt(form.places_total) || 8,
      });
      toast.success("Session créée");
      setOpen(false);
      setForm({ date: "", time: "10:00", title: "", duree: "2h", mode: "Visioconférence", description: "", places_total: 8 });
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const remove = async (id) => {
    if (!window.confirm("Supprimer cette session et toutes ses réservations ?")) return;
    try {
      await api.delete(`/admin/formations/sessions/${id}`);
      toast.success("Session supprimée");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const bookingsBySession = (sid) => bookings.filter((b) => b.session_id === sid);

  return (
    <div className="min-h-full">
      <PageHeader
        eyebrow="Formations"
        title="Sessions & réservations"
        sub="Gérez le calendrier de formations et consultez toutes les inscriptions."
        right={
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="admin-new-session-btn" className="h-11 bg-[#002FA7] hover:bg-[#002175] text-white font-semibold">
                <Plus className="h-4 w-4 mr-2" /> Nouvelle session
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-lg">
              <DialogHeader><DialogTitle className="font-display text-2xl font-black tracking-tight">Créer une session</DialogTitle></DialogHeader>
              <form onSubmit={submit} className="space-y-4" data-testid="session-form">
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label className="text-xs uppercase tracking-[0.2em] font-semibold">Date</Label>
                    <Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="session-date" className="h-11" required />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs uppercase tracking-[0.2em] font-semibold">Heure</Label>
                    <Input type="time" value={form.time} onChange={(e) => setForm({ ...form, time: e.target.value })} data-testid="session-time" className="h-11" required />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-[0.2em] font-semibold">Titre</Label>
                  <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="session-title" className="h-11" required />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label className="text-xs uppercase tracking-[0.2em] font-semibold">Durée</Label>
                    <Input value={form.duree} onChange={(e) => setForm({ ...form, duree: e.target.value })} data-testid="session-duree" className="h-11" placeholder="2h" />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs uppercase tracking-[0.2em] font-semibold">Mode</Label>
                    <Select value={form.mode} onValueChange={(v) => setForm({ ...form, mode: v })}>
                      <SelectTrigger data-testid="session-mode" className="h-11"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Visioconférence">Visioconférence</SelectItem>
                        <SelectItem value="Présentiel Paris">Présentiel Paris</SelectItem>
                        <SelectItem value="Présentiel Lyon">Présentiel Lyon</SelectItem>
                        <SelectItem value="Présentiel Marseille">Présentiel Marseille</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-[0.2em] font-semibold">Places totales</Label>
                  <Input type="number" value={form.places_total} onChange={(e) => setForm({ ...form, places_total: e.target.value })} data-testid="session-places" className="h-11" />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-[0.2em] font-semibold">Description</Label>
                  <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={3} data-testid="session-desc" />
                </div>
                <DialogFooter>
                  <Button type="submit" data-testid="session-submit" className="bg-[#002FA7] hover:bg-[#002175] text-white">Créer la session</Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        }
      />

      <div className="p-6 lg:p-10 space-y-6">
        {/* Summary */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <Stat icon={Calendar} label="Sessions programmées" value={sessions.length} testid="stat-sessions" />
          <Stat icon={Users} label="Réservations totales" value={bookings.length} testid="stat-bookings" />
          <Stat icon={Users} label="Places disponibles" value={sessions.reduce((s, x) => s + (x.places_restantes || 0), 0)} testid="stat-places" />
          <Stat icon={Calendar} label="Sessions complètes" value={sessions.filter((s) => (s.places_restantes || 0) === 0).length} testid="stat-full" />
        </div>

        {/* Sessions list */}
        <div className="bg-white border border-[#DEE2E6]" data-testid="sessions-list">
          <div className="p-4 border-b border-[#DEE2E6] text-xs uppercase tracking-[0.25em] font-semibold">Sessions ({sessions.length})</div>
          {sessions.length === 0 ? (
            <div className="p-8 text-center text-sm text-[#868E96]">Aucune session. Créez-en une.</div>
          ) : sessions.map((s) => {
            const d = new Date(s.date);
            const bks = bookingsBySession(s.id);
            return (
              <div key={s.id} className="p-5 border-b border-[#DEE2E6] last:border-b-0" data-testid={`session-${s.id}`}>
                <div className="flex items-start justify-between gap-4 flex-wrap">
                  <div className="flex-1 min-w-[260px]">
                    <div className="text-xs uppercase tracking-[0.25em] font-semibold text-[#002FA7] mb-1">
                      {JOURS_LONGS[d.getDay()]} {d.getDate()} {MOIS[d.getMonth()]} {d.getFullYear()} · {String(d.getHours()).padStart(2, "0")}h{String(d.getMinutes()).padStart(2, "0")} · {s.duree} · {s.mode}
                    </div>
                    <div className="font-display font-bold text-lg">{s.title}</div>
                    {s.description && <p className="text-xs text-[#495057] mt-1">{s.description}</p>}
                    <div className="mt-2 text-xs text-[#495057]">
                      <span className={s.places_restantes === 0 ? "text-[#E60000] font-bold" : "text-[#059669] font-semibold"}>
                        {s.places_restantes}/{s.places_total} places restantes
                      </span> · {bks.length} réservation{bks.length > 1 ? "s" : ""}
                    </div>
                  </div>
                  <Button size="sm" variant="outline" onClick={() => remove(s.id)} data-testid={`session-del-${s.id}`} className="border-[#E60000] text-[#E60000] hover:bg-[#E60000] hover:text-white">
                    <Trash2 className="h-3 w-3" />
                  </Button>
                </div>
                {bks.length > 0 && (
                  <div className="mt-4 pt-4 border-t border-[#DEE2E6] space-y-1">
                    <div className="text-[10px] uppercase tracking-[0.25em] font-semibold text-[#868E96] mb-2">Réservations</div>
                    {bks.map((b) => (
                      <div key={b.id} className="text-xs grid grid-cols-4 gap-2 py-1" data-testid={`booking-${b.id}`}>
                        <span className="font-semibold">{b.prenom} {b.nom}</span>
                        <span className="text-[#495057]">{b.email}</span>
                        <span className="text-[#495057] font-mono">{b.telephone}</span>
                        <span className="text-[#495057] truncate">{b.entreprise || b.entreprise_code || "—"}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function Stat({ icon: Icon, label, value, testid }) {
  return (
    <div data-testid={testid} className="bg-white border border-[#DEE2E6] p-5">
      <Icon className="h-4 w-4 text-[#002FA7] mb-2" />
      <div className="font-display text-3xl font-black tracking-tighter">{value}</div>
      <div className="text-[10px] uppercase tracking-[0.25em] text-[#868E96] font-semibold mt-1">{label}</div>
    </div>
  );
}
